import Icon from "@ant-design/icons";
import { Button } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { EMPTY_OBJECT } from "/src/constants";
import useHttpClient from "/src/hooks/useHttpClient";
import useOrg from "/src/hooks/apis/useOrg";

// The connect token has a short server-side TTL (a couple of minutes) and is
// embedded in the t.me deep link. Refresh it well inside that window so a click
// always carries a fresh token instead of failing with "لینک منقضی شده است".
const TOKEN_REFRESH_MS = 60 * 1000;

function TelegramSvg() {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z" />
    </svg>
  );
}

function TelegramIcon(props) {
  return <Icon component={TelegramSvg} {...props} />;
}

function TelegramButton({ data = EMPTY_OBJECT }) {
  const httpClient = useHttpClient();
  const { data: org } = useOrg();
  const botName = org?.config?.telegramSupportUsername;
  const [token, setToken] = useState("");
  const isLinked = Boolean(data.telegramId);

  const fetchToken = useCallback(() => {
    return httpClient.post("/auth/ephemeral-token/").then(
      ({
        data: {
          data: { token },
        },
      }) => setToken(token),
      // On failure keep the previous token so the link stays usable.
      () => {}
    );
  }, [httpClient]);

  useEffect(() => {
    if (isLinked) return undefined;
    fetchToken();
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") fetchToken();
    }, TOKEN_REFRESH_MS);
    const refreshIfVisible = () => {
      if (document.visibilityState === "visible") fetchToken();
    };
    document.addEventListener("visibilitychange", refreshIfVisible);
    window.addEventListener("focus", refreshIfVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", refreshIfVisible);
      window.removeEventListener("focus", refreshIfVisible);
    };
  }, [isLinked, fetchToken]);

  if (isLinked) {
    return (
      <Button
        href={`https://t.me/${botName}`}
        target="_blank"
        rel="noreferrer noopener"
        icon={<TelegramIcon />}
        size="large"
      >
        تلگرام
      </Button>
    );
  }

  return (
    <Button
      href={`https://t.me/${botName}?start=${token}`}
      disabled={!token}
      target="_blank"
      rel="noreferrer noopener"
      // Fire-and-forget refresh: does NOT preventDefault and does NOT await, so
      // this click navigates normally (no popup blocking) while the token is
      // refreshed for the next attempt — so a retry after a stale token works.
      onClick={() => fetchToken()}
      icon={<TelegramIcon />}
      size="large"
    >
      اتصال اکانت تلگرام
    </Button>
  );
}

export default TelegramButton;
